import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { MessageSquare, X, Send, Sparkles, ArrowUpRight, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const SUGGESTED = [
  "What's my STP rate this month?",
  "How many vacant units do I have?",
  "Unpaid rent for July?",
  "Draft an N4 letter for John at 123 Main",
];

export default function SmartOperatorBubble() {
  const { user } = useAuth();
  const location = useLocation();
  const isLandlord = user?.role === "landlord";
  const PUBLIC_PREFIXES = ["/login", "/signup", "/forgot", "/reset", "/accept-invite", "/ad/", "/about"];
  // Routes that already have their own primary input at the bottom — hide the
  // floating FAB so it doesn't cover Send buttons on mobile.
  const HIDE_FAB_PREFIXES = ["/messages", "/tenant-letters", "/community", "/smart-operator"];
  const isPublicRoute = location.pathname === "/" || PUBLIC_PREFIXES.some((p) => location.pathname.startsWith(p));
  const hideOnRoute = HIDE_FAB_PREFIXES.some((p) => location.pathname.startsWith(p));
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  // Session-scoped dismiss so the operator can hide the FAB when it's
  // in the way; a page refresh brings it back.
  const [hidden, setHidden] = useState(() => {
    try { return localStorage.getItem("crc_smart_op_hidden") === "1"; } catch { return false; }
  });
  const dismiss = (e) => {
    e.stopPropagation();
    e.preventDefault();
    try { localStorage.setItem("crc_smart_op_hidden", "1"); } catch {}
    setHidden(true);
  };
  const [sessionId, setSessionId] = useState(() => {
    try { return localStorage.getItem("crc_smart_operator_sid") || ""; } catch (_) { return ""; }
  });
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  if (!isLandlord || isPublicRoute || hideOnRoute || hidden) return null;

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setSending(true);
    try {
      const { data } = await api.post("/smart-operator/ask", {
        message: msg,
        session_id: sessionId || undefined,
      });
      if (data.session_id && data.session_id !== sessionId) {
        setSessionId(data.session_id);
        try { localStorage.setItem("crc_smart_operator_sid", data.session_id); } catch (_) { /* noop */ }
      }
      setMessages((m) => [...m, { role: "assistant", content: data.reply, actions: data.actions || [] }]);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Couldn't reach Smart Operator";
      setMessages((m) => [...m, { role: "assistant", content: `Sorry — ${detail}.`, actions: [] }]);
    } finally {
      setSending(false);
    }
  };

  if (!open) {
    return (
      <div className="fixed bottom-40 sm:bottom-6 right-4 sm:right-5 z-30 group inline-flex items-center">
        <button
          data-testid="smart-operator-fab"
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-2 pl-4 pr-5 py-3 rounded-full bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-lg shadow-emerald-500/30 hover:shadow-emerald-500/40 transition-shadow font-semibold text-sm whitespace-nowrap"
        >
          <Sparkles className="w-4 h-4 flex-none" />
          Smart Operator
        </button>
        <button
          onClick={dismiss}
          data-testid="smart-operator-dismiss"
          aria-label="Hide Smart Operator"
          className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-white text-slate-700 border border-slate-300 shadow-md flex items-center justify-center hover:bg-slate-100 z-10"
        >
          <X className="w-3.5 h-3.5"/>
        </button>
      </div>
    );
  }

  return (
    <div
      data-testid="smart-operator-panel"
      className="fixed bottom-5 right-5 z-40 w-[380px] max-w-[calc(100vw-2rem)] h-[560px] max-h-[calc(100vh-6rem)] rounded-2xl bg-white border border-slate-200 shadow-2xl flex flex-col overflow-hidden"
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-gradient-to-r from-emerald-50 to-sky-50">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 text-white flex items-center justify-center">
            <Sparkles className="w-4 h-4" />
          </div>
          <div className="min-w-0">
            <div className="text-sm font-bold text-slate-900">Smart Operator</div>
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-700">Your portfolio assistant</div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Link
            to="/smart-operator"
            data-testid="smart-operator-fullpage-link"
            className="p-1.5 rounded-md hover:bg-white/60 text-slate-500 hover:text-slate-800"
            aria-label="Open full page"
          >
            <ArrowUpRight className="w-4 h-4" />
          </Link>
          <button
            data-testid="smart-operator-close"
            onClick={() => setOpen(false)}
            className="p-1.5 rounded-md hover:bg-white/60 text-slate-500 hover:text-slate-800"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-slate-50">
        {messages.length === 0 && (
          <div className="space-y-3">
            <div className="text-sm text-slate-600 leading-relaxed">
              Hi {user?.name?.split(" ")[0] || "there"}. Ask me anything about your portfolio — vacancies, rent due, STP rate, or pull up the right page to take action.
            </div>
            <div className="grid gap-2">
              {SUGGESTED.map((s, i) => (
                <button
                  key={i}
                  data-testid={`smart-operator-suggest-${i}`}
                  onClick={() => send(s)}
                  className="text-left px-3 py-2 rounded-lg bg-white border border-slate-200 text-sm text-slate-700 hover:border-emerald-300 hover:bg-emerald-50/40"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m, i) => (
          <MessageBubble key={i} m={m} />
        ))}

        {sending && (
          <div className="flex items-center gap-2 text-xs text-slate-500" data-testid="smart-operator-thinking">
            <Loader2 className="w-3 h-3 animate-spin" /> Smart Operator is thinking…
          </div>
        )}
      </div>

      <form
        onSubmit={(e) => { e.preventDefault(); send(); }}
        className="border-t border-slate-200 p-3 bg-white flex items-end gap-2"
      >
        <textarea
          data-testid="smart-operator-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
          rows={1}
          placeholder="Ask about your portfolio…"
          className="flex-1 resize-none px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:border-emerald-400 min-h-[40px] max-h-[120px]"
        />
        <button
          type="submit"
          disabled={!input.trim() || sending}
          data-testid="smart-operator-send"
          className="p-2.5 rounded-lg bg-emerald-600 text-white disabled:opacity-50 hover:bg-emerald-700 flex-none"
        >
          <Send className="w-4 h-4" />
        </button>
      </form>
    </div>
  );
}

export function MessageBubble({ m }) {
  const isUser = m.role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
        isUser ? "bg-emerald-600 text-white" : "bg-white border border-slate-200 text-slate-800"
      }`}>
        {m.content}
        {!isUser && Array.isArray(m.actions) && m.actions.length > 0 && (
          <div className="mt-2.5 flex flex-wrap gap-1.5">
            {m.actions.map((a, i) => (
              <Link
                key={i}
                to={a.route}
                data-testid={`smart-operator-action-${i}`}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-semibold hover:bg-emerald-100"
              >
                {a.label} <ArrowUpRight className="w-3 h-3" />
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
